import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';

/**
 * Quick date-range dropdown for the Presentation detail toolbar.
 * Mirrors the legacy `wf-audit/.../presenationItem/DateRangePicker.tsx`
 * shortcut menu: Today / Yesterday / This Week / Last Week / This
 * Month / Last Month / Last 7 Days / Last 30 Days / Date Range.
 *
 * The picker owns no data filtering itself — it just bubbles the
 * chosen preset through `onChange` so the parent can update the
 * executions card / KPI rollup once those wire to a date-aware
 * `/v1/kpi-values` query. Until then it's a visual control matching
 * legacy parity.
 */
export type PresentationDateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'last-7-days'
  | 'last-30-days'
  | 'custom';

const PRESET_LABEL: Record<PresentationDateRangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'this-week': 'This Week',
  'last-week': 'Last Week',
  'this-month': 'This Month',
  'last-month': 'Last Month',
  'last-7-days': 'Last 7 Days',
  'last-30-days': 'Last 30 Days',
  custom: 'Date Range',
};

export interface PresentationDateRangeDropdownProps {
  value: PresentationDateRangePreset;
  onChange: (next: PresentationDateRangePreset) => void;
  /** Optional renderer override for the trigger label (e.g. show a
   *  `MM/DD/YYYY – MM/DD/YYYY` range when `custom`). */
  renderTriggerLabel?: (preset: PresentationDateRangePreset) => string;
}

export function PresentationDateRangeDropdown({
  value,
  onChange,
  renderTriggerLabel,
}: PresentationDateRangeDropdownProps): JSX.Element {
  const label = renderTriggerLabel ? renderTriggerLabel(value) : PRESET_LABEL[value];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-foreground/15 px-2.5 py-1 text-sm text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          <span className="max-w-[180px] truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-foreground/50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>Quick Range</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(
          [
            'today',
            'yesterday',
            'this-week',
            'last-week',
            'this-month',
            'last-month',
            'last-7-days',
            'last-30-days',
          ] as const
        ).map((p) => (
          <DropdownMenuItem key={p} onClick={() => onChange(p)}>
            {PRESET_LABEL[p]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onChange('custom')}>
          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
          {PRESET_LABEL.custom}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
